import {
  ActionError,
  Button,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  SavedIndicator,
  toast,
} from '@westhetics/ui'
import { useRef, type FormEvent } from 'react'
import { Link, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { FormSkeleton } from '@/features/clinic-profile/blocks'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { isApiError } from '@/lib/api'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useNetwork, useUpdateNetwork, type Network } from './api'
import { identificationLine } from './labels'
import { NetworkClinicsBlock, NetworkIdentificationBlock, NetworkSharingBlock } from './blocks'
import {
  EMPTY_NETWORK_FORM,
  NETWORK_FORM_KEYS,
  networkFieldKey,
  networkInput,
  validateNetworkForm,
  type NetworkFormState,
} from './form'

/**
 * Detalhe da rede no painel root (docs/areas/administracao.md, "Detalhe da rede"; RN-ADM-54,
 * RT-06). Arquétipo: cadastro. Nome, compartilhamento e composição são salvos juntos, pelo Salvar do
 * cabeçalho, com o conjunto completo de clínicas (P-ADM-80). Não há excluir: rede sem clínica está
 * desativada (P-ADM-82).
 */
export function NetworkPage() {
  const { id } = useParams()
  const query = useNetwork(id)
  // O formulário nasce da rede carregada; outra rede, outro formulário.
  return <NetworkForm key={query.data?.id ?? 'loading'} id={id ?? ''} query={query} />
}

function formFrom(network: Network): NetworkFormState {
  return { name: network.name, sharesPatients: network.sharesPatients, clinics: network.clinics }
}

function NetworkForm({ id, query }: { id: string; query: ReturnType<typeof useNetwork> }) {
  const network = query.data
  const save = useUpdateNetwork(id)
  const form = useFormState<NetworkFormState>(
    () => (network ? formFrom(network) : EMPTY_NETWORK_FORM),
    validateNetworkForm,
  )
  const formRef = useRef<HTMLFormElement>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!network) return
    if (!form.check(NETWORK_FORM_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    save.mutate(networkInput(form.values), {
      onSuccess: (saved) => {
        // O formulário passa a ser o que o servidor gravou (nome aparado, clínicas em ordem).
        form.update(formFrom(saved))
        form.markSaved()
        toast(`Rede ${saved.name} salva — vale na próxima leitura das clínicas`)
      },
      onError: (error) => {
        form.refuse(placeFieldErrors(error, networkFieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  const failure = save.isError ? placeFieldErrors(save.error, networkFieldKey) : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)
  const existing =
    save.error && isApiError(save.error) && save.error.code === 'conflict'
      ? (save.error.details.existing as { id: string } | undefined)
      : undefined

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Redes', href: routes.admin.networks },
          { label: network ? network.name : '…' },
        ]}
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to={routes.admin.networks}>Voltar</Link>
          </Button>
        }
      />
      <Page>
        <ReadingColumn wide>
          {query.isError ? (
            <>
              <PageTitle title="Rede" />
              <LoadError
                className="mt-6"
                title="Não conseguimos abrir esta rede"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            </>
          ) : !network ? (
            <>
              <PageTitle title="Rede" />
              <FormSkeleton blocks={3} />
            </>
          ) : (
            <>
              <PageTitle title={network.name} meta={identificationLine(network)} />

              <div className="mt-6 flex flex-col gap-4">
                {showFailure ? (
                  <ActionError
                    title="Não foi possível salvar a rede"
                    description={
                      failure.unplaced.length > 0
                        ? failure.unplaced.join(' ')
                        : `${errorMessage(save.error)} Nada mudou; o que você alterou continua na tela.`
                    }
                    actions={
                      existing && existing.id !== network.id ? (
                        <Button variant="outline" size="sm" asChild>
                          <Link to={routes.admin.network(existing.id)}>Abrir a rede existente</Link>
                        </Button>
                      ) : undefined
                    }
                  />
                ) : null}

                <NetworkIdentificationBlock form={form} />
                <NetworkSharingBlock form={form} savedValue={network.sharesPatients} />
                <NetworkClinicsBlock form={form} error={form.errors.clinics} />

                <FormFooter
                  status={
                    <SavedIndicator
                      savedAt={new Date(network.updatedAt)}
                      dirty={form.dirty}
                      saving={save.isPending}
                    />
                  }
                  secondary={
                    <Button variant="outline" asChild>
                      <Link to={routes.admin.networks}>Cancelar</Link>
                    </Button>
                  }
                  primary={
                    <Button type="submit" loading={save.isPending} loadingLabel="Salvando…">
                      Salvar
                    </Button>
                  }
                />
              </div>
            </>
          )}
        </ReadingColumn>
      </Page>
    </form>
  )
}
